import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import GoBackButton from '../../../../../components/GoBackButton';
import { ERoutes } from '../../../../../routes';
import {
  IFabricationMockup,
  IPavement
} from '../../../../../store/ducks/fabrication-mockups/types';
import ConfigMockup from '../ConfigMockup';
import Pavements from './Pavements';
import {
  Container,
  MockupContainer,
  PavementContainer,
  EditMockupHeader,
  TitleMockupContainer
} from './styles';
import { ApplicationState } from '../../../../../store/index';
import { makeid } from '../../../../../utils/formatStrings';
import { saveMockup } from '../../../../../store/ducks/fabrication-mockups/actions';
import { Button } from '@gerdau/hefesto/Button';
import { Icon } from '@gerdau/hefesto/Icon';
import { Title } from '@gerdau/hefesto/Title';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';

const MockupView: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const mockupsState = useSelector(
    (appState: ApplicationState) => appState.fabricationMockups
  );
  const isSaving = useSelector(
    (appState: ApplicationState) =>
      appState.fabricationMockups.saveMockupState?.loading ||
      appState.fabricationMockups.editPavementState?.loading ||
      mockupsState.loading ||
      false
  );
  const [newestAddedPavementId, setNewestAddedPavementId] = useState<string>();
  const [canShowConfigModal, setCanShowConfigModal] = useState(false);

  const getMockupById = (): IFabricationMockup => {
    const allMockups = mockupsState.mockups;
    const mockup = allMockups.find(
      (m) => m.id === mockupsState.saveMockupState?.editingMockupId
    );

    return JSON.parse(
      JSON.stringify({
        ...mockup,
        pavements: JSON.parse(JSON.stringify(mockup?.pavements || []))
      })
    );
  };

  const [selectedMockup, setSelectedMockup] = useState<IFabricationMockup>(
    getMockupById()
  );
  const [needToUpdateSelectedMockup, setNeedToUpdateSelectedMockup] =
    useState(false);

  const updateSelectedMockupIdNeed = () => {
    if (
      needToUpdateSelectedMockup &&
      !isSaving &&
      mockupsState.saveMockupState?.success
    ) {
      setNeedToUpdateSelectedMockup(false);
      setSelectedMockup(getMockupById());
      setNeedToSortPavements(true);
    }
  };

  useEffect(() => {
    updateSelectedMockupIdNeed();
  });

  const [pavements, setPavements] = useState<IPavement[]>([]);
  const [needToSortPavements, setNeedToSortPavements] = useState(true);

  useEffect(() => {
    if (selectedMockup && needToSortPavements) {
      const sortedPavements = [...selectedMockup.pavements].sort(
        (a, b) => b.position - a.position
      );
      setPavements(sortedPavements);
      setNeedToSortPavements(false);
    }
  }, [selectedMockup, needToSortPavements]);

  const onSaveMockup = (
    name: string,
    hasRoof: boolean,
    hasCovering: boolean,
    typicalFloorPlan: string,
    otherTypicalFloorPlan: string,
    mockupPavements: IPavement[],
    id: number
  ) => {
    if (!mockupsState.contractNumber) {
      return;
    }

    dispatch(
      saveMockup(
        mockupsState.contractNumber,
        name,
        hasRoof,
        hasCovering,
        typicalFloorPlan,
        otherTypicalFloorPlan,
        mockupPavements,
        id
      )
    );
    setNeedToUpdateSelectedMockup(true);
  };

  const onSaveMockupClick = () => {
    const {
      name,
      hasRoof,
      hasCovering,
      typicalFloorPlan,
      otherTypicalFloorPlan,
      id
    } = selectedMockup;
    onSaveMockup(
      name,
      hasRoof,
      hasCovering,
      typicalFloorPlan,
      otherTypicalFloorPlan,
      selectedMockup.pavements,
      id
    );
  };

  const onRemovePavement = (pavementIdx: number) => {
    const newPavements = pavements;
    newPavements.splice(pavementIdx, 1);
    updatePavementsPositions(newPavements);
    const newSelectedMockup = selectedMockup;
    newSelectedMockup.pavements = newPavements;
    setSelectedMockup(newSelectedMockup);
    setNeedToSortPavements(true);
  };

  const onAddPavement = (pavementAddedAt: 'top' | 'bottom') => {
    const getPavementName = (pavementPosition: number): string => {
      if (pavementPosition > 0) {
        setNeedToSortPavements(true);
        const defaultPavementName =
          selectedMockup.typicalFloorPlan || 'Pavimento';
        return `${defaultPavementName} ${pavementPosition}`;
      } else {
        return `Subsolo ${Math.abs(pavementPosition)}`;
      }
    };

    const newPavementPosition =
      pavementAddedAt === 'top'
        ? pavements[0].position + 1
        : pavements[pavements.length - 1].position - 1;

    const newPavementId = makeid(6);

    const newPavement: IPavement = {
      position: newPavementPosition,
      name: getPavementName(newPavementPosition),
      id: newPavementId,
      isNewPavement: true
    };

    const newSelectedMockup = selectedMockup;

    if (newSelectedMockup.pavements) {
      newSelectedMockup.pavements.push(newPavement);
    } else {
      newSelectedMockup.pavements = [newPavement];
    }
    setNewestAddedPavementId(newPavementId);
    setSelectedMockup(newSelectedMockup);
    setNeedToSortPavements(true);
  };

  const updatePavementsPositions = (pavementsToSort: IPavement[]) => {
    const numberOfPavementsBelowGround = pavementsToSort.filter(
      (p) => p.position < 0
    ).length;
    pavementsToSort.reverse();
    pavements.forEach((pavement, idx) => {
      pavement.position = idx - numberOfPavementsBelowGround;
    });
    pavementsToSort.reverse();
  };

  const onEditPavement = (editedPavement: IPavement) => {
    const pavementIdx = selectedMockup.pavements.findIndex(
      (p) => p.id && p.id === editedPavement.id
    );
    const newSelectedMockup = selectedMockup;
    if (pavementIdx === -1) {
      if (newSelectedMockup.pavements) {
        newSelectedMockup.pavements.push(editedPavement);
      } else {
        newSelectedMockup.pavements = [editedPavement];
      }
    } else {
      newSelectedMockup.pavements[pavementIdx] = editedPavement;
    }
    setSelectedMockup(newSelectedMockup);
    setNeedToSortPavements(true);
  };

  const hasAnyPavementChanged = (): boolean => {
    const mockup = getMockupById();
    if (mockup.pavements.length !== selectedMockup.pavements.length) {
      return true;
    } else {
      return selectedMockup.pavements.some((p) => {
        const mockupPavement = mockup?.pavements.find((pav) => pav.id === p.id);
        return (
          !mockupPavement ||
          mockupPavement.name !== p.name ||
          mockupPavement.position !== p.position
        );
      });
    }
  };

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    pathname,
    true
  );

  const onClickGoBackBtn = () => {
    dispatch(
      sendTagging({
        screenName,
        itemName: 'button:voltar',
        itemDescription: `${itemDescription}_voltar`,
        event: 'click'
      })
    );
    history.push(ERoutes.PROJECT_MOCKUP);
  };

  return (
    <>
      <Container>
        <EditMockupHeader>
          <GoBackButton title="Voltar" onClick={onClickGoBackBtn} />
          <TitleMockupContainer>
            <Title level={1}>Editar maquete</Title>
            <span className="subtitle">
              Crie e edite sua maquete conforme a sua obra. Acompanhe os pedidos
              por etapa da obra, associe seus projetos. Após associar os pedidos
              a uma
              <br /> maquete, não será possível editar a maquete.
            </span>
          </TitleMockupContainer>
        </EditMockupHeader>
        <div className="mockup-content">
          <MockupViewHeaderFooter
            selectedMockup={selectedMockup}
            onEditClick={() => setCanShowConfigModal(true)}
            onSaveClick={onSaveMockupClick}
            isSaving={isSaving}
            disabled={!hasAnyPavementChanged() || isSaving}
          />
          <PavementContainer>
            <header className="pavements-header">
              <div className="pavements-content">
                <Title level={4}>Pavimentos:</Title>
                <Button
                  name="addPavement"
                  variant="secondary"
                  onClick={() => {
                    dispatch(
                      sendTagging({
                        screenName,
                        itemName: 'button:adicionar_pavimento',
                        itemDescription: `${itemDescription}_adicionarPavimento`,
                        event: 'click'
                      })
                    );
                    onAddPavement('top');
                  }}
                  disabled={isSaving}
                  icon={<Icon option="PlusCircle" />}
                  iconPosition="right"
                >
                  Adicionar pavimento
                </Button>
              </div>
              <Title level={4}>Status dos pavimentos:</Title>
            </header>

            {pavements && (
              <Pavements
                mockupId={selectedMockup.id}
                isSaving={isSaving}
                pavements={pavements}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                contractNumber={mockupsState.contractNumber!}
                onSavePavement={onEditPavement}
                onRemovePavement={onRemovePavement}
                onAddPavement={() => {
                  onAddPavement('bottom');
                  setTimeout(() => {
                    window.scrollTo({
                      top: document.body.scrollHeight,
                      behavior: 'smooth'
                    });
                  }, 100);
                }}
                newestAddedPavementId={newestAddedPavementId}
                cleanNewestAddedPavementId={() =>
                  setNewestAddedPavementId(undefined)
                }
              />
            )}
          </PavementContainer>
          <MockupViewHeaderFooter
            selectedMockup={selectedMockup}
            onEditClick={() => setCanShowConfigModal(true)}
            onSaveClick={onSaveMockupClick}
            isSaving={isSaving}
            disabled={!hasAnyPavementChanged() || isSaving}
          />
        </div>
      </Container>

      {canShowConfigModal && mockupsState.contractNumber && (
        <ConfigMockup
          allMockupNames={mockupsState.mockups.map((m) => m.name)}
          mockup={selectedMockup}
          contractNumber={mockupsState.contractNumber}
          onEdit={onSaveMockup}
          onCancel={() => setCanShowConfigModal(false)}
        />
      )}
    </>
  );
};

interface IMockupViewHeaderFooterProps {
  selectedMockup: IFabricationMockup;
  onEditClick: () => void;
  onSaveClick: () => void;
  isSaving: boolean;
  disabled: boolean;
}

const MockupViewHeaderFooter: React.FC<IMockupViewHeaderFooterProps> = ({
  selectedMockup,
  onEditClick,
  onSaveClick,
  isSaving,
  disabled
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    pathname,
    true
  );

  return (
    <MockupContainer>
      <>
        <Title level={2}>{selectedMockup.name}</Title>
        <div className="button save-mockup-btn">
          <Button
            name="editInfo"
            onClick={() => {
              dispatch(
                sendTagging({
                  screenName,
                  itemName: 'button:editar_informacoes',
                  itemDescription: `${itemDescription}_editarInformacoes`,
                  event: 'click'
                })
              );
              onEditClick();
            }}
            disabled={isSaving}
            variant="secondary"
          >
            Editar informações
          </Button>

          <Button
            name="saveEdit"
            onClick={() => {
              dispatch(
                sendTagging({
                  screenName,
                  itemName: 'button:salvar_edicoes',
                  itemDescription: `${itemDescription}_salvarEdicoes`,
                  event: 'click'
                })
              );
              onSaveClick();
            }}
            disabled={disabled}
            loading={isSaving}
          >
            Salvar edições
          </Button>
        </div>
      </>
    </MockupContainer>
  );
};
export default MockupView;
